import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SkillTreeNodeToast from './SkillTreeNodeToast';

/**
 * SkillTreeToastContext — single shared instance so a skill-tree node click
 * and an owned-cluster atom click both open the SAME popover (no duplicate
 * instances).
 *
 * Trigger model (Edward 2026-06-25):
 *  - HOVER a node opens the compact gold card transiently (a tooltip). Moving
 *    the cursor off the node — and off the card — closes it after a short
 *    grace so you can travel from the node onto the card to use its actions.
 *  - CLICK pins the card (it "stays toggled"): it no longer auto-closes on
 *    mouse-leave. Clicking the same pinned node again closes it (toggle).
 *    Clicking outside / Esc also closes (handled inside the toast).
 *
 * Surfaces that only call openNodeToast (click) — e.g. the owned-cluster
 * atoms — keep the click-to-open / click-again-to-close behavior; every card
 * they open is pinned.
 *
 * View-only surfaces (Edward 2026-06-25): the equip bar (SkillTreeOwnedCluster)
 * passes `{ readOnly: true }` to open/hover, which renders the card as a pure
 * tooltip — its Equip/Unequip/Switch/Buy affordances are suppressed because the
 * loadout strip displays the equipped set, it isn't where you modify it. The
 * skill-tree canvas omits the flag, so its mutation actions are unchanged.
 *
 * Anchor: the toast positions itself at the node's screen rect (captured from
 * the triggering event's currentTarget) or the last known cursor position,
 * then auto-flips if it would overflow the viewport edges.
 */
const SkillTreeToastContext = createContext({
  openNodeToast: () => {},
  hoverNodeToast: () => {},
  closeToast: () => {},
  currentNodeId: null,
});

// Tooltip timing. OPEN_DELAY is "hover intent" — keeps cards from flashing as
// the cursor sweeps across the canvas to reach a target. Once a card is up,
// hovering another node switches instantly (warm). CLOSE_GRACE must comfortably
// exceed the node→card cursor traversal (TOAST_GAP ~10px) so the card survives
// the hand-off; the card's own onMouseEnter cancels the pending close.
const OPEN_DELAY = 120;
const CLOSE_GRACE = 240;

function nodeIdOf(node) {
  if (!node) return null;
  return node.id ?? node._stm?.id ?? null;
}

// Resolve the toast anchor + node rect from a MouseEvent OR {x,y} OR nothing.
// Read synchronously at call time (the synthetic event's currentTarget is
// nulled after the handler returns, and the open may be deferred by a timer).
function resolveAnchor(eventOrAnchor, lastMouse) {
  let anchor;
  let nodeRect = null;
  if (eventOrAnchor && typeof eventOrAnchor === 'object') {
    if ('clientX' in eventOrAnchor && 'clientY' in eventOrAnchor) {
      anchor = { x: eventOrAnchor.clientX, y: eventOrAnchor.clientY };
      const target = eventOrAnchor.currentTarget;
      if (target && typeof target.getBoundingClientRect === 'function') {
        const r = target.getBoundingClientRect();
        if (r && (r.width > 0 || r.height > 0)) {
          nodeRect = { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
        }
      }
    } else if ('x' in eventOrAnchor && 'y' in eventOrAnchor) {
      anchor = eventOrAnchor;
    }
  }
  if (!anchor) anchor = { ...lastMouse };
  return { anchor, nodeRect };
}

export function SkillTreeToastProvider({ children }) {
  const [state, setState] = useState({ node: null, anchor: null, nodeRect: null, pinned: false, readOnly: false, asFloat: false });
  const lastMouseRef = useRef({ x: 0, y: 0 });
  // Mirror state for timer callbacks (avoids stale closures without re-creating
  // the callbacks on every state change).
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) { clearTimeout(openTimerRef.current); openTimerRef.current = null; }
  }, []);
  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  }, []);
  useEffect(() => () => { clearOpenTimer(); clearCloseTimer(); }, [clearOpenTimer, clearCloseTimer]);

  // Track the cursor so click handlers that don't pass the event (legacy
  // paths) can still anchor the toast.
  useEffect(() => {
    const handler = (e) => {
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener('mousemove', handler, { passive: true });
    return () => document.removeEventListener('mousemove', handler);
  }, []);

  // CLICK = pin. Toggles closed when the same already-pinned node is clicked.
  // `options.readOnly` marks a view-only trigger surface (the equip bar): the
  // card opens as a pure tooltip with its mutation affordances suppressed
  // (Edward 2026-06-25 — "the equip bar isnt supposed to provide any
  // modification functionality"). The skill-tree canvas omits it, so its
  // Equip/Unequip/Switch/Buy actions are unchanged.
  const openNodeToast = useCallback((node, eventOrAnchor, options) => {
    if (!node) return;
    const readOnly = !!(options && options.readOnly);
    // `options.asFloat` (skill-tree canvas / map node click) renders the card as a
    // MOVABLE floating toast over the map - same core element/palette as the
    // hotbar's readOnly card + the equip action, draggable with no scrim so the map
    // stays live. The centered `asModal` option was RETIRED 2026-07-10 (the float is
    // now the ONE detail surface on every viewport). Only the pinned click carries
    // asFloat; the transient hover peek stays anchored.
    const asFloat = !!(options && options.asFloat);
    clearOpenTimer();
    clearCloseTimer();
    const { anchor, nodeRect } = resolveAnchor(eventOrAnchor, lastMouseRef.current);
    // Is a movable float currently rendered? The float decision lives in the
    // render (SkillTreeNodeToast: readOnly hotbar clicks float on desktop but
    // DON'T pass options.asFloat, so prev.asFloat is false for them) - so key
    // off the actual DOM, not the state flag, to catch the readOnly-hotbar float.
    const floatOpen = typeof document !== 'undefined' && !!document.querySelector('.stn-toast--float');
    setState((prev) => {
      const nodeId = nodeIdOf(node);
      // Click the currently-open + pinned node -> close. (A transient/hover
      // card on the same node instead gets pinned in place.) EXCEPTION: the
      // movable float is CLOSE-VIA-X-ONLY (Edward 2026-07-10 consolidation) -
      // re-clicking its node must NOT close it (the X foot button is the sole
      // exit); it just re-seeds/re-centers via the fresh anchor.
      if (prev.node && nodeIdOf(prev.node) === nodeId && prev.pinned && !prev.asFloat && !floatOpen) {
        return { node: null, anchor: null, nodeRect: null, pinned: false, readOnly: false, asFloat: false };
      }
      return { node, anchor, nodeRect, pinned: true, readOnly, asFloat };
    });
  }, [clearOpenTimer, clearCloseTimer]);

  // HOVER = transient. `node` truthy on enter, null on leave. `options.readOnly`
  // (equip bar) carries through so the transient card is also a pure tooltip.
  const hoverNodeToast = useCallback((node, eventOrAnchor, options) => {
    if (node) {
      const readOnly = !!(options && options.readOnly);
      // Enter a node. Never disturb a pinned card.
      clearCloseTimer();
      const cur = stateRef.current;
      if (cur.pinned) return;
      const { anchor, nodeRect } = resolveAnchor(eventOrAnchor, lastMouseRef.current);
      if (cur.node) {
        // A transient card is already up — switch to the new node instantly.
        clearOpenTimer();
        setState({ node, anchor, nodeRect, pinned: false, readOnly });
      } else {
        // Cold start — wait out the hover-intent delay before showing.
        clearOpenTimer();
        openTimerRef.current = setTimeout(() => {
          openTimerRef.current = null;
          setState((prev) => (prev.pinned ? prev : { node, anchor, nodeRect, pinned: false, readOnly }));
        }, OPEN_DELAY);
      }
    } else {
      // Leave a node. Cancel a pending open; schedule a graced close unless pinned.
      clearOpenTimer();
      const cur = stateRef.current;
      if (cur.pinned || !cur.node) return;
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        setState((prev) => (prev.pinned ? prev : { node: null, anchor: null, nodeRect: null, pinned: false, readOnly: false }));
      }, CLOSE_GRACE);
    }
  }, [clearOpenTimer, clearCloseTimer]);

  // Cursor entered the card itself — keep a transient card alive while in use.
  const onCardEnter = useCallback(() => { clearCloseTimer(); }, [clearCloseTimer]);
  // Cursor left the card — close a transient card after the grace.
  const onCardLeave = useCallback(() => {
    if (stateRef.current.pinned) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setState((prev) => (prev.pinned ? prev : { node: null, anchor: null, nodeRect: null, pinned: false, readOnly: false }));
    }, CLOSE_GRACE);
  }, [clearCloseTimer]);

  const closeToast = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setState({ node: null, anchor: null, nodeRect: null, pinned: false, readOnly: false });
  }, [clearOpenTimer, clearCloseTimer]);

  const value = useMemo(
    () => ({
      openNodeToast,
      hoverNodeToast,
      closeToast,
      currentNodeId: state.node ? nodeIdOf(state.node) : null,
    }),
    [openNodeToast, hoverNodeToast, closeToast, state.node],
  );

  return (
    <SkillTreeToastContext.Provider value={value}>
      {children}
      {state.node && (
        <SkillTreeNodeToast
          node={state.node}
          anchor={state.anchor}
          nodeRect={state.nodeRect}
          pinned={state.pinned}
          readOnly={state.readOnly}
          asFloat={state.asFloat}
          onClose={closeToast}
          onPointerEnter={onCardEnter}
          onPointerLeave={onCardLeave}
        />
      )}
    </SkillTreeToastContext.Provider>
  );
}

export function useSkillTreeToast() {
  return useContext(SkillTreeToastContext);
}
